import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from 'zite-auth-sdk';
import { getCourseModuleDetail, saveModuleProgress, GetCourseModuleDetailOutputType } from 'zite-endpoints-sdk';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ChevronRight, CheckCircle, Play, AlertTriangle, BookOpen, Clock, ShieldAlert, FileText, WifiOff, Wifi, Save } from 'lucide-react';
import { useDebouncedCallback } from 'use-debounce';
import { toast } from 'sonner';

type ModuleData = GetCourseModuleDetailOutputType;

function SaveIndicator({ state }: { state: 'saved' | 'saving' | 'unsaved' | 'offline' }) {
  if (state === 'offline') return (
    <span className="text-xs text-destructive flex items-center gap-1"><WifiOff className="w-3 h-3" /> Offline</span>
  );
  if (state === 'saving') return (
    <span className="text-xs text-primary flex items-center gap-1 animate-pulse"><Save className="w-3 h-3" /> Saving…</span>
  );
  if (state === 'unsaved') return (
    <span className="text-xs text-muted-foreground flex items-center gap-1"><Save className="w-3 h-3" /> Unsaved</span>
  );
  return (
    <span className="text-xs text-primary flex items-center gap-1"><Wifi className="w-3 h-3" /> Saved</span>
  );
}

function SafetyWarningBanner({ text }: { text: string }) {
  if (!text) return null;
  return (
    <div className="bg-destructive/10 border border-destructive/30 rounded-xl p-4 flex gap-3 my-4">
      <ShieldAlert className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
      <div>
        <p className="text-sm font-bold text-destructive mb-1">⚠ Safety Warning</p>
        <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">{text}</p>
      </div>
    </div>
  );
}

function VideoPlayer({ url, requiredPercent, onProgress, initialPercent }: {
  url: string; requiredPercent: number; onProgress: (p: number) => void; initialPercent: number;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [watchedPercent, setWatchedPercent] = useState(initialPercent);
  const required = requiredPercent > 0 ? requiredPercent : 80;
  const met = watchedPercent >= required;

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const handler = () => {
      if (v.duration > 0) {
        const pct = Math.round((v.currentTime / v.duration) * 100);
        setWatchedPercent(prev => {
          const next = Math.max(prev, pct);
          if (next > prev) onProgress(next);
          return next;
        });
      }
    };
    v.addEventListener('timeupdate', handler);
    return () => v.removeEventListener('timeupdate', handler);
  }, [onProgress]);

  if (!url) return (
    <div className="aspect-video bg-muted rounded-xl flex items-center justify-center border border-dashed border-border">
      <div className="text-center px-4">
        <Play className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
        <p className="text-sm font-medium text-muted-foreground">Training video not yet available</p>
        <p className="text-xs text-muted-foreground mt-1">HeyGen video will appear here once published</p>
      </div>
    </div>
  );

  const isYouTube = url.includes('youtube.com') || url.includes('youtu.be');
  if (isYouTube) {
    const videoId = url.includes('youtu.be') ? url.split('/').pop() : new URLSearchParams(new URL(url).search).get('v');
    return (
      <div className="aspect-video rounded-xl overflow-hidden border border-border">
        <iframe src={`https://www.youtube.com/embed/${videoId}`} className="w-full h-full" allowFullScreen allow="accelerometer; autoplay; clipboard-write; encrypted-media" />
      </div>
    );
  }

  return (
    <div className="relative">
      <video ref={videoRef} src={url} controls playsInline className="w-full rounded-xl aspect-video bg-muted border border-border" preload="metadata" />
      <div className="mt-2 flex items-center gap-2">
        <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
          <div className="h-2 bg-primary rounded-full transition-all" style={{ width: `${Math.min(watchedPercent, 100)}%` }} />
        </div>
        <span className={`text-xs font-medium ${met ? 'text-primary' : 'text-muted-foreground'}`}>
          {watchedPercent}%{met ? ' ✓' : ` / ${required}%`}
        </span>
      </div>
    </div>
  );
}

function SectionContent({ section }: { section: { title: string; content: string; type: string } }) {
  return (
    <div className="prose prose-sm max-w-none text-foreground">
      <div className="text-sm leading-relaxed whitespace-pre-wrap">{section.content || 'Content coming soon…'}</div>
    </div>
  );
}

export default function CourseModuleLearner() {
  const { enrollmentId, moduleId } = useParams<{ enrollmentId: string; moduleId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<ModuleData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentSection, setCurrentSection] = useState(0);
  const [completedSections, setCompletedSections] = useState(0);
  const [videoPercent, setVideoPercent] = useState(0);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveState, setSaveState] = useState<'saved' | 'saving' | 'unsaved' | 'offline'>('saved');
  const [online, setOnline] = useState(navigator.onLine);

  // Track online status
  useEffect(() => {
    const handleOnline = () => { setOnline(true); setSaveState(prev => prev === 'offline' ? 'unsaved' : prev); };
    const handleOffline = () => { setOnline(false); setSaveState('offline'); };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => { window.removeEventListener('online', handleOnline); window.removeEventListener('offline', handleOffline); };
  }, []);

  useEffect(() => {
    if (!user || !enrollmentId || !moduleId) return;
    getCourseModuleDetail({ enrollmentId, moduleId })
      .then(d => {
        setData(d);
        setCompletedSections(d.progress.sectionsCompleted);
        setVideoPercent(d.progress.videoPercent);
        setConfirmed(d.progress.learningConfirmed);
        if (d.progress.sectionsCompleted > 0 && d.progress.sectionsCompleted < d.module.sections.length) {
          setCurrentSection(d.progress.sectionsCompleted);
        }
      })
      .catch(e => setError(e?.message || 'Could not load module'))
      .finally(() => setLoading(false));
  }, [user, enrollmentId, moduleId]);

  const debouncedSave = useDebouncedCallback(async (sections: number, vidPct: number) => {
    if (!enrollmentId || !moduleId || !online) return;
    setSaveState('saving');
    const res = await saveModuleProgress({ enrollmentId, moduleId, sectionsCompleted: sections, videoPercent: vidPct }).catch(() => null);
    setSaveState(res ? 'saved' : 'unsaved');
  }, 2000);

  const markSectionComplete = useCallback((sectionIndex: number) => {
    const newCompleted = Math.max(completedSections, sectionIndex + 1);
    setCompletedSections(newCompleted);
    setSaveState(online ? 'unsaved' : 'offline');
    debouncedSave(newCompleted, videoPercent);
  }, [completedSections, videoPercent, debouncedSave, online]);

  const handleVideoProgress = useCallback((pct: number) => {
    setVideoPercent(prev => {
      const next = Math.max(prev, pct);
      if (next > prev) {
        setSaveState(online ? 'unsaved' : 'offline');
        debouncedSave(completedSections, next);
      }
      return next;
    });
  }, [completedSections, debouncedSave, online]);

  const handleConfirm = async () => {
    if (!enrollmentId || !moduleId || confirmed) return;
    setSaving(true);
    const res = await saveModuleProgress({ enrollmentId, moduleId, learningConfirmed: true }).catch(() => null);
    if (res?.success) {
      setConfirmed(true);
      toast.success('Learning confirmed');
    } else {
      toast.error('Could not save confirmation. Check your connection and try again.');
    }
    setSaving(false);
  };

  const goNext = () => {
    if (!data) return;
    if (currentSection < data.module.sections.length - 1) {
      markSectionComplete(currentSection);
      setCurrentSection(s => s + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goPrev = () => {
    if (currentSection > 0) setCurrentSection(s => s - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) return (
    <div className="max-w-lg mx-auto px-4 py-6 space-y-4">
      <Skeleton className="h-6 w-48" /><Skeleton className="aspect-video" /><Skeleton className="h-40" />
    </div>
  );

  if (error || !data) return (
    <div className="max-w-lg mx-auto px-4 py-12 text-center">
      <ShieldAlert className="w-12 h-12 text-destructive mx-auto mb-3" />
      <h2 className="font-semibold text-foreground mb-1">Cannot Access Module</h2>
      <p className="text-sm text-muted-foreground mb-4">{error || 'This module is not available.'}</p>
      <Link to={`/train/${enrollmentId}`} className="text-sm text-primary hover:underline">← Back to Course</Link>
    </div>
  );

  const { module: mod } = data;
  const sections = mod.sections;
  const isLastSection = currentSection === sections.length - 1;
  const allSectionsComplete = completedSections >= sections.length;
  const videoMet = !mod.videoUrl || videoPercent >= mod.videoRequiredPercent;
  const canConfirm = allSectionsComplete && videoMet && !confirmed;
  const canTakeQuiz = confirmed && data.quiz.canRetake && data.quiz.questionCount > 0;
  const quizPassed = data.quiz.attempts.some(a => a.passed);

  return (
    <div className="max-w-lg mx-auto pb-24">
      {/* Sticky header */}
      <div className="px-4 py-3 border-b border-border bg-card sticky top-0 z-40">
        <div className="flex items-center justify-between">
          <Link to={`/train/${enrollmentId}`} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline truncate max-w-[140px]">{data.courseTitle}</span>
          </Link>
          <SaveIndicator state={saveState} />
        </div>

        {/* Section progress */}
        <div className="flex gap-1 mt-2">
          {sections.map((_, i) => (
            <button key={i} onClick={() => { if (i <= completedSections) setCurrentSection(i); }}
              className={`flex-1 h-2 rounded-full transition-colors ${
                i < completedSections ? 'bg-primary' : i === currentSection ? 'bg-primary/50' : 'bg-muted'
              }`} />
          ))}
        </div>
      </div>

      {/* Module header */}
      <div className="px-4 pt-4 pb-2">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-bold text-primary bg-primary/10 px-2 py-0.5 rounded">{mod.displayOrder}</span>
          <span className="text-xs text-muted-foreground">Module {data.moduleIndex + 1} of {data.totalModules}</span>
        </div>
        <h1 className="text-lg font-bold text-foreground">{mod.displayLabel}</h1>
        {mod.estimatedMinutes > 0 && (
          <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
            <Clock className="w-3 h-3" /> ~{mod.estimatedMinutes} min
          </p>
        )}
      </div>

      {/* Learning objectives - show on first section */}
      {mod.learningOutcome && currentSection === 0 && (
        <div className="px-4 py-2">
          <div className="bg-primary/5 border border-primary/20 rounded-xl p-4">
            <p className="text-xs font-bold text-primary uppercase tracking-wider mb-2">Learning Objectives</p>
            <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">{mod.learningOutcome}</p>
          </div>
        </div>
      )}

      {/* Video - show on first section */}
      {mod.videoUrl !== undefined && currentSection === 0 && (
        <div className="px-4 py-2">
          <VideoPlayer url={mod.videoUrl} requiredPercent={mod.videoRequiredPercent} onProgress={handleVideoProgress} initialPercent={videoPercent} />
        </div>
      )}

      {/* Safety warnings - first section */}
      {currentSection === 0 && <div className="px-4"><SafetyWarningBanner text={mod.safetyWarnings} /></div>}

      {/* Section content */}
      {sections.length > 0 && (
        <div className="px-4 py-3">
          <div className="flex items-center gap-2 mb-3">
            <BookOpen className="w-4 h-4 text-muted-foreground" />
            <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
              Section {currentSection + 1} of {sections.length}
            </span>
            {currentSection < completedSections && <CheckCircle className="w-3.5 h-3.5 text-primary" />}
          </div>

          <h2 className="text-base font-semibold text-foreground mb-3">{sections[currentSection]?.title || 'Content'}</h2>
          <SectionContent section={sections[currentSection] || { title: '', content: mod.contentBody || 'Content coming soon…', type: 'text' }} />
        </div>
      )}

      {/* Common mistakes - last section */}
      {isLastSection && mod.commonMistakes && (
        <div className="px-4 py-2">
          <div className="bg-muted rounded-xl p-4">
            <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">⚠ Common Mistakes to Avoid</p>
            <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">{mod.commonMistakes}</p>
          </div>
        </div>
      )}

      {/* Reference materials - last section */}
      {isLastSection && mod.referenceMaterials && (
        <div className="px-4 py-2">
          <div className="border border-border rounded-xl p-4 flex items-start gap-3">
            <FileText className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-1">Reference Materials</p>
              <p className="text-sm text-foreground">{mod.referenceMaterials}</p>
            </div>
          </div>
        </div>
      )}

      {/* Learning confirmation */}
      {isLastSection && allSectionsComplete && (
        <div className="px-4 py-4">
          {!confirmed ? (
            <div className="border-2 border-dashed border-primary/30 rounded-xl p-5">
              <p className="text-sm font-medium text-foreground mb-4 leading-relaxed">
                I confirm that I have completed and understood the learning content in this module.
              </p>
              <Button onClick={handleConfirm} disabled={saving || !canConfirm}
                className="w-full bg-primary text-primary-foreground h-12 text-base touch-manipulation">
                {saving ? 'Confirming…' : 'Confirm Understanding'}
              </Button>
              {!videoMet && mod.videoUrl && (
                <p className="text-xs text-destructive mt-3 text-center">
                  Watch at least {mod.videoRequiredPercent}% of the training video first ({videoPercent}% watched)
                </p>
              )}
            </div>
          ) : (
            <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 flex items-center gap-3">
              <CheckCircle className="w-6 h-6 text-primary shrink-0" />
              <div>
                <p className="text-sm font-semibold text-primary">Learning Confirmed</p>
                <p className="text-xs text-muted-foreground">
                  {data.progress.confirmedAt ? new Date(data.progress.confirmedAt).toLocaleString('en-GB') : ''}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Quiz section */}
      {isLastSection && confirmed && (
        <div className="px-4 py-3">
          {quizPassed ? (
            <div className="bg-primary/10 rounded-xl p-5 text-center">
              <CheckCircle className="w-10 h-10 text-primary mx-auto mb-2" />
              <p className="font-bold text-foreground text-lg">Quiz Passed</p>
              <p className="text-sm text-muted-foreground mt-1">
                Score: {data.quiz.attempts.find(a => a.passed)?.score}% · {data.quiz.attempts.length} attempt(s)
              </p>
            </div>
          ) : canTakeQuiz ? (
            <Button onClick={() => navigate(`/train/${enrollmentId}/module/${moduleId}/quiz`)}
              className="w-full bg-primary text-primary-foreground text-base py-3.5 h-auto touch-manipulation">
              Start Quiz ({data.quiz.questionCount} questions · {data.quiz.passMark}% to pass)
            </Button>
          ) : data.quiz.questionCount === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              No quiz questions configured for this module yet.
            </p>
          ) : null}

          {/* Attempt history */}
          {data.quiz.attempts.length > 0 && (
            <div className="mt-3 space-y-1">
              <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Attempt History</p>
              {data.quiz.attempts.map(a => (
                <div key={a.attemptNumber} className="flex items-center justify-between text-xs py-1.5 border-b border-border last:border-0">
                  <span className="text-muted-foreground">Attempt {a.attemptNumber}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-foreground font-medium">{a.score}%</span>
                    {a.passed ? (
                      <span className="text-primary font-medium">Passed</span>
                    ) : (
                      <span className="text-destructive font-medium">Failed</span>
                    )}
                    {a.criticalFailed && <AlertTriangle className="w-3 h-3 text-destructive" />}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Fixed bottom nav */}
      <div className="fixed bottom-0 left-0 right-0 bg-card border-t border-border p-3 flex gap-3 z-50 max-w-lg mx-auto">
        <Button variant="outline" onClick={goPrev} disabled={currentSection === 0}
          className="flex-1 h-12 touch-manipulation">
          Back
        </Button>
        {!isLastSection ? (
          <Button onClick={goNext} className="flex-1 bg-primary text-primary-foreground h-12 touch-manipulation">
            Continue <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        ) : !allSectionsComplete ? (
          <Button onClick={() => markSectionComplete(currentSection)}
            className="flex-1 bg-primary text-primary-foreground h-12 touch-manipulation">
            Complete Section
          </Button>
        ) : quizPassed ? (
          <Button onClick={() => navigate(`/train/${enrollmentId}`)}
            className="flex-1 bg-primary text-primary-foreground h-12 touch-manipulation">
            Back to Course
          </Button>
        ) : null}
      </div>
    </div>
  );
}
